/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useContext, useEffect } from 'react';
import { AppContext } from 'Context';
import cn from 'classnames';
import useSWR from 'swr';
import { dataFetcher, endpoints } from 'Api';
import { Grid } from '@mui/material';
import { Add, Category } from '@mui/icons-material';
import { isNewUIEnabled } from 'hooks/useNewUI';
import styles from './index.module.css';

// Inline Lucide icons (MIT) — matches the V2 theme used across the app.
const lucideProps = (size = 18) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
});
const IconPlus = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <path d="M5 12h14" />
    <path d="M12 5v14" />
  </svg>
);
const IconLayers = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <path d="M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z" />
    <path d="M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12" />
    <path d="M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17" />
  </svg>
);
const IconTrash = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <path d="M3 6h18" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" x2="10" y1="11" y2="17" />
    <line x1="14" x2="14" y1="11" y2="17" />
  </svg>
);

const CreateSubCategory = ({ DragIcon, setSubCategoryDiv, content_type, isOpenSubCategoryDiv }) => {
  // The V2 restyle (inline Lucide icons + blue accent chrome) ships only to the
  // new UI. Classic users keep the legacy MUI Add/Category icons and text remove.
  const newUI = isNewUIEnabled();
  const [val, setVal] = useState(undefined);
  const {
    setAppSnackbar,
    langText,
    setShowAddSubCategoryModal,
    subCategoryRefreshTrigger
  } = useContext(AppContext);
  const [subCategory, setSubCategory] = useState(undefined);
  const commonText = langText?.common;
  const layoutText = langText?.layout;
  const [subCatId, setSubCatId] = useState(undefined);
  const [editing, setEditing] = useState(false);
  const { isValidating: gettingSubCategories, mutate: getSubCategories } = useSWR(
    endpoints.getMySubCategory,
    dataFetcher,
    {
      revalidateOnMount: true,
      onSuccess: ({ success, data }) => {
        if (success) {
          setSubCategory(data?.map(({ id, title }) => ({ name: title, value: id })));
        } else {
          setAppSnackbar({
            isVisible: true,
            type: 'error',
            message: 'Oops! Something went wrong while fetching your subcategories',
          });
        }
      },
      onError: (error) => {
        const responseData = error?.response?.data;
        setAppSnackbar({ isVisible: true, type: 'error', message: responseData || 'Oops! Something went wrong while fetching your subcategories' });
      },
    }
  );

  const { isValidating: removingSubCategory, mutate: deleteSubCategory } = useSWR([
    endpoints.deleteMySubCategory, subCatId
  ], {
    fetcher: (url, subCatId) => dataFetcher(url, {
      subCatId
    }),
    onSuccess: ({ success }) => {
      if (success) {
        getSubCategories();
        setAppSnackbar({
          isVisible: true,
          message: 'Sub-Category was removed',
        });
      } else {
        setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong' });
      }
    },
    onError: (error) => {
      const responseData = error?.response?.data?.message;
      setAppSnackbar({ isVisible: true, type: 'error', message: responseData || 'Oops! Something went wrong' });
    },
  });

  useEffect(() => {
    getSubCategories();
  }, []);

  // Refresh subcategories when a new one is created from the modal
  useEffect(() => {
    if (subCategoryRefreshTrigger > 0) {
      getSubCategories();
    }
  }, [subCategoryRefreshTrigger]);

  const removeSubCategory = (name, value) => {
    setVal(name);
    setSubCatId(value);
    if (value) {
      deleteSubCategory();
    }
  };

  return (
    <div className={styles.subCategoryGroup}>
      <div className={styles.subCategoryHeader}>
        <div
          className={cn({ [styles.dragIconContainer]: true, [styles.dragIconContainerType]: true })}
        >
          <DragIcon className={styles.dragIcon} style={{ opacity: 0 }} />
        </div>
        <div
          className={styles.contentTypeTitle}
          onClick={() => { setSubCategoryDiv(true); }}
        >
          <div className={styles.titleStyle}>{content_type}</div>
        </div>
        <div className={styles.contentGroupBody}>
          <div style={{ textAlign: 'center' }} className={styles.contentGroupBodyCell}>
            <div
              className={cn({ [styles.editContentTypeRef]: true, [styles.editContentTypeRefPosition]: true })}
              onClick={() => { setSubCategoryDiv(!isOpenSubCategoryDiv); }}
            >
              <div className={styles.editContentType}>{isOpenSubCategoryDiv ? (commonText?.back || 'Back') : (commonText?.edit || 'Edit')}</div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.addNewCategoryWrap}>
        {/* Add New Subcategory Button */}
        {newUI ? (
          <div
            onClick={() => { setShowAddSubCategoryModal(true); }}
            className={styles.addSubCatBtn}
          >
            <IconPlus size={18} className={styles.addSubCatIcon} />
            <span className={styles.addSubCatLabel}>
              {layoutText?.addNewSubCategory || 'Add New Subcategory'}
            </span>
          </div>
        ) : (
          <div
            onClick={() => { setShowAddSubCategoryModal(true); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '12px 16px',
              backgroundColor: '#f8fafc',
              borderRadius: 10,
              cursor: 'pointer',
              marginBottom: 16,
              border: '1px dashed #5aa5ef',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#eff6ff';
              e.currentTarget.style.borderColor = '#3b82f6';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#f8fafc';
              e.currentTarget.style.borderColor = '#5aa5ef';
            }}
          >
            <Add style={{ fontSize: 20, marginRight: 10, color: '#5aa5ef' }} />
            <span style={{
              fontSize: 14,
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 600,
              color: '#5aa5ef'
            }}
            >
              {layoutText?.addNewSubCategory || 'Add New Subcategory'}
            </span>
          </div>
        )}

        {/* Existing Subcategories List */}
        <div className={styles.existingSubCatWrap}>
          {
            subCategory?.map(({ name, value }) => (
              <Grid container className={newUI ? styles.subCatDiv : styles.subCatDivLegacy} key={value}>
                {newUI ? (
                  <>
                    <Grid item xs={9} style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                      <span className={styles.subCatIconChip}>
                        <IconLayers size={15} />
                      </span>
                      <span className={styles.nameCell}>{name}</span>
                    </Grid>
                    <Grid
                      onClick={() => { removeSubCategory(name, value); }}
                      item
                      className={styles.removeSubCatButton}
                    >
                      <IconTrash size={13} className={styles.removeSubCatIcon} />
                      <span>{removingSubCategory && value === subCatId ? (commonText?.removing || 'Removing') : (commonText?.remove || 'Remove')}</span>
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={9} style={{ display: 'flex', alignItems: 'center' }}>
                      <Category style={{ fontSize: 16, marginRight: 8, color: '#5aa5ef' }} />
                      <span className={styles.nameCell}>{name}</span>
                    </Grid>
                    <Grid onClick={() => { removeSubCategory(name, value); }} item xs={3} className={styles.removeSubCatButtonLegacy}>
                      {removingSubCategory && value === subCatId ? (commonText?.removing || 'Removing') : (commonText?.remove || 'Remove')}
                    </Grid>
                  </>
                )}
              </Grid>
            ))
          }
        </div>
      </div>

    </div>
  );
};

export default CreateSubCategory;
